import { Button, Loader, Modal, Text } from '@mantine/core';
import { IconDownload, IconFile } from '@tabler/icons-react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import { VideoPlayer } from 'src/Components/Video/VideoPlayer';
import { VideoDto } from 'src/Services/API/generated';

import styles from './DocumentViewerModal.module.scss';
import { DocumentType, Props } from './DocumentViewerModal.types';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const getDocumentType = (fileName: string, video?: VideoDto | null): DocumentType => {
  if (video) return 'video';

  const extension = fileName.split('.').pop()?.toLowerCase() ?? '';

  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
  const pdfExtensions = ['pdf'];
  const officeExtensions = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];

  if (imageExtensions.includes(extension)) return 'image';
  if (pdfExtensions.includes(extension)) return 'pdf';
  if (officeExtensions.includes(extension)) return 'office';

  return 'unknown';
};

const DocumentViewerModal = ({ open, fileUrl, fileName, video, onClose }: Props) => {
  const { t } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [numPages, setNumPages] = useState<number>(0);
  const [pdfError, setPdfError] = useState(false);

  const documentType = useMemo(() => getDocumentType(fileName, video), [fileName, video]);

  useEffect(() => {
    if (open) {
      setImageLoaded(false);
      setNumPages(0);
      setPdfError(false);
    }
  }, [open, fileUrl]);

  const handleDownload = useCallback(() => {
    window.open(fileUrl, '_blank');
  }, [fileUrl]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const renderContent = () => {
    switch (documentType) {
      case 'image':
        return (
          <img
            src={fileUrl}
            onLoad={() => setImageLoaded(true)}
            className={styles.image}
            alt={fileName}
            style={{
              visibility: imageLoaded ? 'visible' : 'hidden',
            }}
          />
        );

      case 'pdf':
        if (pdfError) {
          return (
            <div className={styles.unsupportedContainer}>
              <IconFile size={64} className={styles.unsupportedIcon} />
              <Text className={styles.fileName}>{fileName}</Text>
              <Text c="dimmed">{t('documentViewer.pdfError', 'Impossible de charger le PDF')}</Text>
              <Button
                leftSection={<IconDownload size={16} />}
                onClick={handleDownload}
                className={styles.downloadButton}
              >
                {t('documentViewer.download', 'Télécharger')}
              </Button>
            </div>
          );
        }

        return (
          <div className={styles.pdfContainer}>
            <Document
              file={fileUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={() => setPdfError(true)}
              loading={
                <div className={styles.loaderContainer}>
                  <Loader size="lg" />
                </div>
              }
              className={styles.pdfDocument}
            >
              {Array.from(new Array(numPages), (_, index) => (
                <Page
                  key={`page_${index + 1}`}
                  pageNumber={index + 1}
                  className={styles.pdfPage}
                  width={Math.min(800, window.innerWidth - 100)}
                />
              ))}
            </Document>
          </div>
        );

      case 'video':
        if (!video) return null;
        return (
          <div className={styles.videoContainer}>
            <VideoPlayer video={video} />
          </div>
        );

      case 'office':
        return (
          <iframe
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`}
            className={styles.iframe}
            title={fileName}
          />
        );

      default:
        return (
          <div className={styles.unsupportedContainer}>
            <IconFile size={64} className={styles.unsupportedIcon} />
            <Text className={styles.fileName}>{fileName}</Text>
            <Text c="dimmed">
              {t(
                'documentViewer.unsupported',
                "L'aperçu n'est pas disponible pour ce type de fichier"
              )}
            </Text>
            <Button
              leftSection={<IconDownload size={16} />}
              onClick={handleDownload}
              className={styles.downloadButton}
            >
              {t('documentViewer.download', 'Télécharger')}
            </Button>
          </div>
        );
    }
  };

  return (
    <Modal
      opened={open}
      onClose={onClose}
      withCloseButton={true}
      size={documentType === 'unknown' ? 'md' : '90%'}
      title={fileName}
      styles={{
        title: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: 'calc(100% - 40px)',
        },
        content: {
          overflow: 'hidden',
        },
      }}
    >
      <div className={styles.container}>{renderContent()}</div>
    </Modal>
  );
};

export default DocumentViewerModal;
